import { useEffect, useState, useCallback } from 'react';
import { CheckCircle, Search, Download, ChevronLeft, ChevronRight, Eye, Trash2 } from 'lucide-react';
import { apiFetch } from '../lib/api';
import type { Donation } from '../types';
import { useNavigate } from 'react-router-dom';

interface DonationFilters {
  status: string;
  paymentMethod: string;
  dateRange: string;
  startDate: string;
  endDate: string;
  search: string;
}

export default function AdminDonations() {
  const navigate = useNavigate();
  const [donations, setDonations] = useState<Donation[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<DonationFilters>({
    status: '',
    paymentMethod: '',
    dateRange: '',
    startDate: '',
    endDate: '',
    search: '',
  });

  const fetchDonations = useCallback(async () => {
    setLoading(true);
    const query = new URLSearchParams();
    query.set('page', String(page));
    query.set('limit', '20');
    if (filters.status) query.set('status', filters.status);
    if (filters.paymentMethod) query.set('paymentMethod', filters.paymentMethod);
    if (filters.startDate) query.set('startDate', filters.startDate);
    if (filters.endDate) query.set('endDate', filters.endDate);
    if (filters.search) query.set('search', filters.search);

    const res = await apiFetch<{ donations: Donation[]; total: number; totalPages: number }>(
      `/api/admin/donations?${query.toString()}`
    );
    if (res.success && res.data) {
      setDonations(res.data.donations);
      setTotalPages(res.data.totalPages);
    }
    setLoading(false);
  }, [page, filters]);

  useEffect(() => {
    fetchDonations();
  }, [fetchDonations]);

  const handleDelete = async (id: number, donationNum: string) => {
    if (!confirm(`確定要刪除捐款 ${donationNum} 嗎？此操作無法撤銷。`)) return;
    const res = await apiFetch(`/api/admin/donations/${id}`, { method: 'DELETE' });
    if (res.success) {
      fetchDonations();
    } else {
      alert(res.error?.message || '刪除失敗');
    }
  };

  const handleExportCSV = () => {
    if (donations.length === 0) return;
    const headers = ['編號', '姓名', '電話', '電郵', '金額', '類型', '方式', '狀態', '匿名', '感謝牆', '收據', '日期', '備註'];
    const rows = donations.map((d) => [
      d.donationNum,
      d.donorName,
      d.donorPhone || '',
      d.donorEmail || '',
      (d.amount / 100).toLocaleString(),
      d.donationType === 'one-time' ? '一次性' : d.donationType === 'monthly-first' ? '月捐首筆' : '月捐續期',
      methodLabel(d.paymentMethod),
      d.paymentStatus === 'pending' ? '待確認' : d.paymentStatus === 'confirmed' ? '已確認' : '失敗',
      d.isAnonymous ? '是' : '否',
      d.displayOnWall ? '是' : '否',
      d.receiptRequested ? '是' : '否',
      new Date(d.createdAt).toLocaleString('zh-HK'),
      d.message || '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `donations_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-amber-100 text-amber-700',
      confirmed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status === 'pending' && '待確認'}
        {status === 'confirmed' && '已確認'}
        {status === 'failed' && '失敗'}
      </span>
    );
  };

  const methodLabel = (m: string) => {
    const labels: Record<string, string> = {
      fps: 'FPS',
      'bank-transfer': '銀行轉賬',
    };
    return labels[m] || m;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">捐款管理</h1>
        <button
          onClick={handleExportCSV}
          disabled={donations.length === 0}
          className="btn-secondary text-sm py-2 px-4 min-h-[40px]"
        >
          <Download className="w-4 h-4" />
          導出 CSV
        </button>
      </div>

      {/* 篩選欄 */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜索姓名 / 編號"
              value={filters.search}
              onChange={(e) => { setFilters({ ...filters, search: e.target.value }); setPage(1); }}
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 bg-white"
          >
            <option value="">全部狀態</option>
            <option value="pending">待確認</option>
            <option value="confirmed">已確認</option>
            <option value="failed">失敗</option>
          </select>
          <select
            value={filters.paymentMethod}
            onChange={(e) => { setFilters({ ...filters, paymentMethod: e.target.value }); setPage(1); }}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 bg-white"
          >
            <option value="">全部方式</option>
            <option value="fps">FPS</option>
            <option value="bank-transfer">銀行轉賬</option>
          </select>
          <select
            value={filters.dateRange}
            onChange={(e) => {
              const range = e.target.value;
              const fmt = (d: Date) =>
                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              const today = fmt(new Date());
              let start = '';
              let end = '';
              if (range === 'today') {
                start = today;
                end = today;
              } else if (range === '7days') {
                const d = new Date();
                d.setDate(d.getDate() - 6);
                start = fmt(d);
                end = today;
              } else if (range === '30days') {
                const d = new Date();
                d.setDate(d.getDate() - 29);
                start = fmt(d);
                end = today;
              } else if (range === 'thisMonth') {
                const d = new Date();
                start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
                end = today;
              } else if (range === 'lastMonth') {
                const d = new Date();
                d.setMonth(d.getMonth() - 1);
                start = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
                const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                end = fmt(lastDay);
              } else if (range === 'custom') {
                start = filters.startDate;
                end = filters.endDate;
              }
              setFilters({ ...filters, dateRange: range, startDate: start, endDate: end });
              setPage(1);
            }}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 bg-white"
          >
            <option value="">全部時間</option>
            <option value="today">今天</option>
            <option value="7days">最近7天</option>
            <option value="30days">最近30天</option>
            <option value="thisMonth">本月</option>
            <option value="lastMonth">上月</option>
            <option value="custom">自定義範圍</option>
          </select>
          {filters.dateRange === 'custom' && (
            <div className="flex gap-2">
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => { setFilters({ ...filters, startDate: e.target.value }); setPage(1); }}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
              />
              <span className="text-gray-400 self-center">~</span>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => { setFilters({ ...filters, endDate: e.target.value }); setPage(1); }}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
              />
            </div>
          )}
        </div>
      </div>

      {/* 表格 */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">編號</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">姓名</th>
                <th className="px-4 py-3 text-right font-medium whitespace-nowrap">金額</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">方式</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">狀態</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">日期</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <span className="inline-block w-full h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : donations.length > 0 ? (
                donations.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-gray-600 whitespace-nowrap">{d.donationNum}</td>
                    <td className="px-4 py-3 text-gray-800 whitespace-nowrap">
                      {d.isAnonymous ? (
                        <span className="text-gray-400">匿名</span>
                      ) : (
                        d.donorName
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                      HK${(d.amount / 100).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{methodLabel(d.paymentMethod)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{statusBadge(d.paymentStatus)}</td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(d.createdAt).toLocaleDateString('zh-HK')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/admin/donations/${d.id}`)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 transition-colors"
                          title="查看詳情"
                        >
                          <Eye className="w-3 h-3" />
                          查看
                        </button>
                        {d.paymentStatus === 'pending' && (
                          <button
                            onClick={() => navigate(`/admin/donations/${d.id}`)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-brand-600 text-white text-xs font-medium hover:bg-brand-700 transition-colors"
                          >
                            <CheckCircle className="w-3 h-3" />
                            核實
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(d.id, d.donationNum)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors"
                          title="刪除"
                        >
                          <Trash2 className="w-3 h-3" />
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    沒有符合條件的記錄
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 分頁 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 px-3">
            第 {page} / {totalPages} 頁
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
